import { Prerequisite } from "@/v2/entities/prerequisite/Prerequisite.model";
import { useSimulator } from "./SimulatorWrapper";
import { useMemo } from "react";
import { Instruction } from "@/v2/entities/instruction/Instruction.model";
import { ReleaseConversation } from "@/v2/entities/release-conversation/ReleaseConversation.model";

export interface TopicChange {
  topicId: number;
  topicName: string;
  isEnabled: boolean;
  isLive: boolean;
  isNew: boolean;
  hasUpdates: boolean;
  instructions: Instruction[];
  prerequisites: Prerequisite[];
  conversations: ReleaseConversation[];
  changes: {
    isEnabled?: boolean;
    brief?: string;
    isKnowledgeEnabled?: boolean;
    isHistoricalTicketsEnabled?: boolean;
    isWikiEnabled?: boolean;
    escalationStrategy?: string;
    instructions?: Instruction[];
    prerequisites?: Prerequisite[];
  };
}

interface GlobalInstructionChange {
  pending: boolean;
  draftContent: string;
  publishedContent: string;
}

interface DraftChanges {
  general: {
    customToneOfVoice?: string;
    escalationStrategy?: string;
    maxBackAndForth?: number;
    lengthOfConversation?: string;
    includeOpening?: boolean;
    includeLink?: boolean;
    strictMode?: boolean;
  };
  topics: TopicChange[];
  globalInstructions: GlobalInstructionChange;
}

const emptyGlobalInstructions: GlobalInstructionChange = {
  pending: false,
  draftContent: "",
  publishedContent: "",
};

const getGlobalInstructionDraftDiff = (
  draftInstructions?: { content: string }[],
  publishedInstructions?: { content: string }[],
  hasPublishedRelease?: boolean
): GlobalInstructionChange => {
  const draftContent = (draftInstructions?.[0]?.content ?? "").trim();
  const publishedContent = (publishedInstructions?.[0]?.content ?? "").trim();

  return {
    pending: Boolean(hasPublishedRelease) && draftContent !== publishedContent,
    draftContent,
    publishedContent,
  };
};

export const useDraft = (): DraftChanges => {
  const { currentRelease, publishedRelease, topics } = useSimulator();

  return useMemo(() => {
    if (!currentRelease) {
      return {
        general: {},
        topics: [],
        globalInstructions: emptyGlobalInstructions,
      };
    }

    const generalChanges: DraftChanges["general"] = {};

    if (!publishedRelease) {
      generalChanges.customToneOfVoice = currentRelease.customToneOfVoice;
      generalChanges.escalationStrategy = currentRelease.escalationStrategy;
      generalChanges.maxBackAndForth = currentRelease.maxBackAndForth;
      generalChanges.lengthOfConversation = currentRelease.lengthOfConversation;
      generalChanges.includeOpening = currentRelease.includeOpening;
      generalChanges.includeLink = currentRelease.includeLink;
      generalChanges.strictMode = currentRelease.strictMode;
    } else {
      if (
        currentRelease.customToneOfVoice !== publishedRelease.customToneOfVoice
      ) {
        generalChanges.customToneOfVoice = currentRelease.customToneOfVoice;
      }

      if (
        currentRelease.escalationStrategy !==
        publishedRelease.escalationStrategy
      ) {
        generalChanges.escalationStrategy = currentRelease.escalationStrategy;
      }

      if (currentRelease.maxBackAndForth !== publishedRelease.maxBackAndForth) {
        generalChanges.maxBackAndForth = currentRelease.maxBackAndForth;
      }

      if (
        currentRelease.lengthOfConversation !==
        publishedRelease.lengthOfConversation
      ) {
        generalChanges.lengthOfConversation =
          currentRelease.lengthOfConversation;
      }

      if (currentRelease.includeOpening !== publishedRelease.includeOpening) {
        generalChanges.includeOpening = currentRelease.includeOpening;
      }

      if (currentRelease.includeLink !== publishedRelease.includeLink) {
        generalChanges.includeLink = currentRelease.includeLink;
      }

      if (currentRelease.strictMode !== publishedRelease.strictMode) {
        generalChanges.strictMode = currentRelease.strictMode;
      }
    }

    const currentTopics = currentRelease.topicReleasePolicies || [];
    const publishedTopics = publishedRelease?.topicReleasePolicies || [];

    const topicChanges: TopicChange[] = [];

    const currentTopicsMap = new Map(
      currentTopics.map((topic) => [topic.topicId, topic])
    );
    const publishedTopicsMap = new Map(
      publishedTopics.map((topic) => [topic.topicId, topic])
    );

    topics?.forEach((topic) => {
      const currentTopic = currentTopicsMap.get(topic.id);
      const publishedTopic = publishedTopicsMap.get(topic.id);

      const changes: TopicChange["changes"] = {};
      let hasChanges = false;

      if (!publishedRelease) {
        if (currentTopic) {
          changes.brief = currentTopic.brief;
          changes.isKnowledgeEnabled = currentTopic.isKnowledgeEnabled;
          changes.isHistoricalTicketsEnabled =
            currentTopic.isHistoricalTicketsEnabled;
          changes.isWikiEnabled = currentTopic.isWikiEnabled;
          changes.escalationStrategy = currentTopic.escalationStrategy;
          changes.instructions = currentTopic.instructions;
          changes.prerequisites = currentTopic.prerequisites;

          hasChanges = !!(
            currentTopic.brief ||
            (currentTopic.instructions &&
              currentTopic.instructions.length > 0) ||
            (currentTopic.prerequisites &&
              currentTopic.prerequisites.length > 0)
          );
        }
      } else {
        const normalizedPublishedBrief = publishedTopic?.brief || "";
        const normalizedCurrentBrief = currentTopic?.brief || "";

        if (normalizedPublishedBrief !== normalizedCurrentBrief) {
          changes.brief = currentTopic?.brief;
          hasChanges = true;
        }

        if (
          publishedTopic?.isKnowledgeEnabled !== undefined &&
          currentTopic?.isKnowledgeEnabled !==
            publishedTopic?.isKnowledgeEnabled
        ) {
          changes.isKnowledgeEnabled = currentTopic?.isKnowledgeEnabled;
          hasChanges = true;
        }

        if (
          publishedTopic?.isHistoricalTicketsEnabled !== undefined &&
          currentTopic?.isHistoricalTicketsEnabled !==
            publishedTopic?.isHistoricalTicketsEnabled
        ) {
          changes.isHistoricalTicketsEnabled =
            currentTopic?.isHistoricalTicketsEnabled;
          hasChanges = true;
        }

        if (
          publishedTopic?.isWikiEnabled !== undefined &&
          currentTopic?.isWikiEnabled !== publishedTopic?.isWikiEnabled
        ) {
          changes.isWikiEnabled = currentTopic?.isWikiEnabled;
          hasChanges = true;
        }

        if (
          publishedTopic?.escalationStrategy !== undefined &&
          currentTopic?.escalationStrategy !==
            publishedTopic?.escalationStrategy
        ) {
          changes.escalationStrategy = currentTopic?.escalationStrategy;
          hasChanges = true;
        }

        // Check for instructions created after publishedRelease.publishedAt
        const newInstructions = currentTopic?.instructions?.filter(
          (instruction) => {
            if (!publishedRelease?.publishedAt) return true;
            const instructionCreatedAt = new Date(
              instruction.createdAt
            ).getTime();
            const publishedAt = new Date(
              publishedRelease.publishedAt
            ).getTime();
            return instructionCreatedAt > publishedAt;
          }
        );

        if (newInstructions && newInstructions.length > 0) {
          changes.instructions = newInstructions;
          hasChanges = true;
        }

        // Check for prerequisites created after publishedRelease.publishedAt
        const newPrerequisites = currentTopic?.prerequisites?.filter(
          (prerequisite) => {
            if (!publishedRelease?.publishedAt) return true;
            const prerequisiteCreatedAt = new Date(
              prerequisite.createdAt
            ).getTime();
            const publishedAt = new Date(
              publishedRelease.publishedAt
            ).getTime();
            return prerequisiteCreatedAt > publishedAt;
          }
        );

        if (newPrerequisites && newPrerequisites.length > 0) {
          changes.prerequisites = newPrerequisites;
          hasChanges = true;
        }
      }

      const conversations = currentRelease.releaseConversations?.filter(
        (conversation) => conversation.topicId === topic.id
      );

      const hasConversationsWithFeedback =
        conversations &&
        conversations.some((conversation) => conversation.feedback !== "NONE");

      if (hasConversationsWithFeedback) {
        hasChanges = true;
      }

      const isNew =
        !currentTopic?.isEnabled &&
        (!currentTopic?.instructions ||
          currentTopic.instructions.length === 0) &&
        (!currentTopic?.prerequisites ||
          currentTopic.prerequisites.length === 0) &&
        !currentTopic?.brief &&
        hasChanges;

      topicChanges.push({
        topicId: topic.id,
        topicName: topic.topic,
        isEnabled: currentTopic?.isEnabled ?? false,
        isLive: publishedTopic?.isEnabled ?? false,
        isNew,
        hasUpdates: hasChanges,
        changes,
        instructions: currentTopic?.instructions || [],
        prerequisites: currentTopic?.prerequisites || [],
        conversations: conversations || [],
      });
    });

    return {
      general: generalChanges,
      topics: topicChanges,
      globalInstructions: getGlobalInstructionDraftDiff(
        currentRelease.globalInstructions,
        publishedRelease?.globalInstructions,
        Boolean(publishedRelease)
      ),
    };
  }, [currentRelease, publishedRelease, topics]);
};
